import type { LineageWorkbenchView, QueryResult } from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import { PAGE_SOURCES, type RepositoryContext, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata, metricValue } from '../metadata';
import { ownerRef } from '../refs';

/**
 * Lineage and mapping workbench (ref: 08_…png).
 *
 * `transformationPreview.isNonMutating` is a literal `true` in the contract, so
 * the fixture cannot accidentally imply that previewing a rule writes anything.
 */

export function createLineageRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async getWorkbench(datasetId?: string): Promise<QueryResult<LineageWorkbenchView>> {
      const scope = applyFilters(dataset, index, user, {});

      const mapping =
        (datasetId
          ? dataset.lineageMappings.find((m) => m.id === datasetId || m.code === datasetId)
          : undefined) ?? dataset.lineageMappings[0];

      const nodes = dataset.lineageNodes.map((node) => ({
        id: node.id,
        column: node.column,
        label: node.label,
        sublabel: node.sublabel,
        recordCount: node.recordCount,
        status: node.status,
        iconToken: node.iconToken,
      }));

      const nodeIdByCode = new Map(dataset.lineageNodes.map((n) => [n.code, n.id]));
      const edges = dataset.lineageEdges
        .map((edge) => {
          const fromNodeId = nodeIdByCode.get(edge.fromNodeCode);
          const toNodeId = nodeIdByCode.get(edge.toNodeCode);
          if (!fromNodeId || !toNodeId) return null;
          return {
            id: edge.id,
            fromNodeId,
            toNodeId,
            mappingRuleId: edge.mappingRuleCode
              ? (mapping?.rules.find((r) => r.code === edge.mappingRuleCode)?.code ?? null)
              : null,
            status: edge.status,
          };
        })
        .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

      const errors = mapping?.validation.filter((v) => v.severity === 'Error') ?? [];
      const warnings = mapping?.validation.filter((v) => v.severity === 'Warning') ?? [];
      const info = mapping?.validation.filter((v) => v.severity === 'Info') ?? [];

      const view: LineageWorkbenchView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-080',
            label: 'Source Datasets',
            value: dataset.lineageNodes.filter((n) => n.column === 'source').length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-081',
            label: 'Canonical Entities',
            value: dataset.lineageNodes.filter((n) => n.column === 'canonical').length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-082',
            label: 'Mapping Rules',
            value: dataset.lineageMappings.reduce((sum, m) => sum + m.rules.length, 0),
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-083',
            label: 'Open Validation Errors',
            value: errors.reduce((sum, e) => sum + e.count, 0),
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-084',
            label: 'Rules Awaiting Approval',
            value: dataset.lineageMappings.filter((m) => m.approvedAt === null).length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
        ],
        nodes,
        edges,
        selectedMapping: mapping
          ? {
              id: mapping.id,
              name: mapping.name,
              sourceLabel: mapping.sourceLabel,
              targetLabel: mapping.targetLabel,
              mappingType: mapping.mappingType,
              status: mapping.status,
              owner: index.peopleByCode.get(mapping.ownerPersonCode)?.displayName ?? 'Unassigned',
              createdOn: mapping.createdOn,
              lastModifiedAt: mapping.lastModifiedAt,
              approvedBy: mapping.approvedByPersonCode
                ? (index.peopleByCode.get(mapping.approvedByPersonCode)?.displayName ?? null)
                : null,
              approvedAt: mapping.approvedAt,
              version: mapping.version,
            }
          : null,
        rules: (mapping?.rules ?? []).map((rule) => ({
          id: rule.code,
          ordinal: rule.ordinal,
          ruleName: rule.ruleName,
          transformationType: rule.transformationType,
          expression: rule.expression,
          isEditable: rule.isEditable && user.allowedModules.includes('ingestion'),
        })),
        transformationPreview: {
          sourceColumns: mapping?.preview.sourceColumns ?? [],
          targetColumns: mapping?.preview.targetColumns ?? [],
          rows: mapping?.preview.rows ?? [],
          isNonMutating: true,
        },
        validation: {
          errors: errors.reduce((sum, e) => sum + e.count, 0),
          warnings: warnings.reduce((sum, e) => sum + e.count, 0),
          info: info.reduce((sum, e) => sum + e.count, 0),
          issues: (mapping?.validation ?? []).map((issue) => ({
            severity: issue.severity,
            rule: issue.rule,
            issue: issue.issue,
            count: issue.count,
            lastOccurredAt: issue.lastOccurredAt,
          })),
        },
        approvalWorkflow: (mapping?.approvalWorkflow ?? []).map((step) => ({
          stage: step.stage,
          actor: step.personCode
            ? (index.peopleByCode.get(step.personCode)?.displayName ?? null)
            : null,
          status: step.status,
          occurredAt: step.occurredAt,
        })),
        stewardAssignments: (mapping?.stewards ?? []).map((steward) => ({
          role: steward.role,
          steward: ownerRef(index, user, steward.personCode),
        })),
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminLineage,
          sources: PAGE_SOURCES.lineage,
          confidence: mapping?.status === 'Published' ? 'High' : 'Low',
          actions: [
            {
              action: 'previewTransformation',
              permission: 'view',
              enabled: true,
            },
            {
              action: 'editRule',
              permission: 'edit',
              enabled: user.allowedModules.includes('ingestion'),
              disabledReason: user.allowedModules.includes('ingestion')
                ? undefined
                : 'Mapping rules are maintained by data stewards.',
            },
            {
              action: 'approveMapping',
              permission: 'approve',
              enabled: false,
              disabledReason: 'A validated mapping is required before approval.',
            },
          ],
        }),
      });
    },
  };
}
